import axios from 'axios';
import { API_BASE_URL } from 'configs/AppConfig';
import { signOutSuccess, authenticated } from 'store/slices/authSlice';
import store from '../store';
import { AUTH_TOKEN, REFRESH_TOKEN } from 'constants/AuthConstant';
import { notification } from 'antd';
import { forceReconnect as wsReconnect, disconnect as wsDisconnect } from '../wsClient';

// Token storage keys
const getStoredToken = () => localStorage.getItem(AUTH_TOKEN);
const getStoredRefreshToken = () => localStorage.getItem(REFRESH_TOKEN);
const setStoredToken = (token) => localStorage.setItem(AUTH_TOKEN, token);
const setStoredRefreshToken = (token) => localStorage.setItem(REFRESH_TOKEN, token);
const clearTokens = () => {
  localStorage.removeItem(AUTH_TOKEN);
  localStorage.removeItem(REFRESH_TOKEN);
};

// Track if we're currently refreshing the token
let isRefreshing = false;
let failedQueue = [];
let refreshTimer = null;

// Decode JWT to get expiry time
const getTokenExpiry = (token) => {
  try {
    const base64Payload = token.split('.')[1];
    const payload = JSON.parse(atob(base64Payload));
    return payload.exp * 1000; // Convert to milliseconds
  } catch (e) {
    return null;
  }
};

// Proactive token refresh - refresh 5 minutes before expiry
const scheduleTokenRefresh = (token) => {
  if (refreshTimer) {
    clearTimeout(refreshTimer);
    refreshTimer = null;
  }

  const expiry = getTokenExpiry(token);
  if (!expiry) return;

  const now = Date.now();
  const timeUntilExpiry = expiry - now;
  const refreshTime = timeUntilExpiry - (5 * 60 * 1000); // Refresh 5 minutes before expiry

  if (refreshTime > 0) {
    console.log(`[TokenRefresh] Scheduling refresh in ${Math.round(refreshTime / 1000 / 60)} minutes`);
    refreshTimer = setTimeout(() => {
      proactiveTokenRefresh();
    }, refreshTime);
  } else if (timeUntilExpiry > 0) {
    // Token expires in less than 5 minutes, refresh now
    console.log('[TokenRefresh] Token expiring soon, refreshing now');
    proactiveTokenRefresh();
  }
};

// Proactive refresh function
const proactiveTokenRefresh = async () => {
  if (isRefreshing) return;
  
  const refreshToken = getStoredRefreshToken();
  const currentToken = getStoredToken();
  
  if (!refreshToken || !currentToken) return;

  isRefreshing = true;
  console.log('[TokenRefresh] Proactively refreshing token...');

  try {
    let userId = null;
    try {
      const base64Payload = currentToken.split('.')[1];
      const payload = JSON.parse(atob(base64Payload));
      userId = payload.id;
    } catch (e) {}

    const refreshResponse = await axios.post(`${API_BASE_URL}/auth/refresh-token`, {
      refreshToken,
      userId,
    }, {
      headers: { 'Authorization': `Bearer ${currentToken}` },
      timeout: 10000,
    });

    const { token: newToken, refreshToken: newRefreshToken, user } = refreshResponse.data;
    
    console.log('[TokenRefresh] Proactive refresh successful');

    setStoredToken(newToken);
    if (newRefreshToken) {
      setStoredRefreshToken(newRefreshToken);
    }

    store.dispatch(authenticated({ token: newToken, user }));
    
    // Schedule next refresh
    scheduleTokenRefresh(newToken);
    
    try {
      wsReconnect();
    } catch (e) {}

  } catch (error) {
    console.error('[TokenRefresh] Proactive refresh failed:', error.message);
    // Don't logout on proactive refresh failure - let the 401 handler deal with it
  } finally {
    isRefreshing = false;
  }
};

// Initialize token refresh scheduling on page load
const initTokenRefresh = () => {
  const token = getStoredToken();
  if (token) {
    scheduleTokenRefresh(token);
  }
};

// Call on module load
setTimeout(initTokenRefresh, 1000);

// Export for use after login
export { scheduleTokenRefresh };

const processQueue = (error, token = null) => {
  failedQueue.forEach(prom => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  });
  failedQueue = [];
};

const service = axios.create({
  baseURL: API_BASE_URL,
  timeout: 60000
});

// API Request interceptor
service.interceptors.request.use(config => {
  const jwtToken = getStoredToken();
  
  if (jwtToken) {
    config.headers['Authorization'] = `Bearer ${jwtToken}`;
  }

  return config;
}, error => {
  notification.error({
    message: 'Request Error',
    description: error.message
  });
  return Promise.reject(error);
});

// API Response interceptor with refresh token logic
service.interceptors.response.use(
  (response) => {
    return response.data;
  },
  async (error) => {
    const originalRequest = error.config;

    // Handle network errors
    if (!error.response) {
      notification.error({
        message: 'Network Error',
        description: 'Please check your internet connection'
      });
      return Promise.reject(error);
    }

    const { status, data } = error.response;

    // Handle 401 Unauthorized - attempt token refresh
    if (status === 401 && !originalRequest._retry) {
      // Check if error is due to expired token (not invalid credentials)
      const isLoginRequest = originalRequest.url?.includes('/auth/login');
      const isRefreshRequest = originalRequest.url?.includes('/auth/refresh-token');
      
      // Don't retry for login or refresh requests
      if (isLoginRequest || isRefreshRequest) {
        if (isLoginRequest) {
          // Don't show notification for login failures - let the login form handle it
          return Promise.reject(error);
        }
        clearTokens();
        store.dispatch(signOutSuccess());
        return Promise.reject(error);
      }

      // If we're already refreshing, queue this request
      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        }).then(token => {
          originalRequest.headers['Authorization'] = `Bearer ${token}`;
          return service(originalRequest);
        }).catch(err => {
          return Promise.reject(err);
        });
      }

      originalRequest._retry = true;
      isRefreshing = true;

      const refreshToken = getStoredRefreshToken();

      if (!refreshToken) {
        clearTokens();
        wsDisconnect(); // Disconnect WebSocket on logout
        store.dispatch(signOutSuccess());
        notification.error({
          message: 'Session Expired',
          description: 'Please login again'
        });
        return Promise.reject(error);
      }

      try {
        console.log('[FetchInterceptor] Attempting token refresh...');
        
        // Get current token to extract user ID (even if expired)
        const currentToken = getStoredToken();
        let userId = null;
        
        // Try to decode user ID from current token
        if (currentToken) {
          try {
            const base64Payload = currentToken.split('.')[1];
            const payload = JSON.parse(atob(base64Payload));
            userId = payload.id;
          } catch (e) {
            console.log('[FetchInterceptor] Could not decode token:', e.message);
          }
        }
        
        // Use a separate axios instance to avoid interceptor loop
        const refreshResponse = await axios.post(`${API_BASE_URL}/auth/refresh-token`, {
          refreshToken,
          userId, // Send userId as fallback
        }, {
          headers: currentToken ? {
            'Authorization': `Bearer ${currentToken}`
          } : {},
          timeout: 10000, // 10 second timeout for refresh
        });

        const { token: newToken, refreshToken: newRefreshToken, user } = refreshResponse.data;
        
        console.log('[FetchInterceptor] Token refresh successful');

        // Store new tokens
        setStoredToken(newToken);
        if (newRefreshToken) {
          setStoredRefreshToken(newRefreshToken);
        }

        // Update Redux state with new token
        store.dispatch(authenticated({ token: newToken, user }));
        
        // Schedule next proactive refresh
        scheduleTokenRefresh(newToken);
        
        // Reconnect WebSocket with new token
        try {
          wsReconnect();
        } catch (e) {
          console.log('[FetchInterceptor] WS reconnect failed:', e);
        }

        // Process queued requests
        processQueue(null, newToken);

        // Retry original request with new token
        originalRequest.headers['Authorization'] = `Bearer ${newToken}`;
        return service(originalRequest);
      } catch (refreshError) {
        console.error('[FetchInterceptor] Token refresh failed:', refreshError.response?.data || refreshError.message);
        processQueue(refreshError, null);
        clearTokens();
        wsDisconnect(); // Disconnect WebSocket on logout
        store.dispatch(signOutSuccess());
        notification.error({
          message: 'Session Expired',
          description: 'Your session has expired. Please login again.'
        });
        // Redirect to login page
        setTimeout(() => {
          if (window.location.pathname !== '/auth/login') {
            window.location.href = '/auth/login';
          }
        }, 1000);
        return Promise.reject(refreshError);
      } finally {
        isRefreshing = false;
      }
    }

    // Handle 403 Forbidden
    if (status === 403) {
      notification.error({
        message: 'Access Denied',
        description: data?.message || 'You do not have permission to access this resource'
      });
      return Promise.reject(error);
    }

    // Handle 404 Not Found
    if (status === 404) {
      notification.error({
        message: 'Not Found',
        description: data?.message || 'The requested resource was not found'
      });
      return Promise.reject(error);
    }

    // Handle 500 Internal Server Error
    if (status === 500) {
      notification.error({
        message: 'Server Error',
        description: 'Something went wrong. Please try again later.'
      });
      return Promise.reject(error);
    }

    // Handle other errors
    if (status >= 400) {
      // Don't show notification for all 400 errors - let components handle specific cases
      console.error('API Error:', status, data);
    }

    return Promise.reject(error);
  }
);

export default service;
